<template>
  <form @submit.prevent="onSubmit">
    <b-row>
      <b-col class="mb-2" cols="12" lg="6">
        <label>วันที่</label>
        <b-input-group>
          <b-form-datepicker v-model="form.from" :max="form.to || maxDate" :min="minDate" required />
          <b-input-group-append is-text>
            ถึง
          </b-input-group-append>
          <b-form-datepicker v-model="form.to" :max="maxDate" :min="form.from || minDate" required />
        </b-input-group>
        <b-form-text>
          ประวัติเก็บย้อนหลังได้ไม่เกิน {{ maxMonths }} เดือน
        </b-form-text>
      </b-col>
      <b-col class="mb-2" cols="12" lg="3" md="6">
        <label>ผู้ให้บริการ</label>
        <b-form-select v-model="form.paymentGatewayId">
          <b-form-select-option :value="null">
            ทั้งหมด
          </b-form-select-option>
          <b-form-select-option v-for="gateway in gatewayOptions" :key="gateway.id" :value="gateway.id">
            {{ gateway.label }}
          </b-form-select-option>
        </b-form-select>
      </b-col>
      <b-col class="mb-2" cols="12" lg="3" md="6">
        <label>ผู้ทำรายการ</label>
        <b-form-select v-model="form.actorId">
          <b-form-select-option :value="null">
            ทั้งหมด
          </b-form-select-option>
          <b-form-select-option :value="SYSTEM_ACTOR_ID">
            ระบบ
          </b-form-select-option>
          <b-form-select-option v-for="actor in actors" :key="actor.id" :value="actor.id">
            {{ actor.name ? `${actor.username} (${actor.name})` : actor.username }}
          </b-form-select-option>
        </b-form-select>
      </b-col>
      <b-col class="mb-2" cols="12" lg="9" md="6" offset-xl="6" xl="3">
        <label>ประเภทรายการ</label>
        <multiselect
          v-model="form.type"
          :close-on-select="false"
          :custom-label="(type) => typeLabel(type)"
          :multiple="true"
          :options="typeOptions"
          :searchable="false"
          placeholder="ทั้งหมด"
          select-label=""
          selected-label=""
          deselect-label=""
        >
          <template slot="selection" slot-scope="{ values, isOpen }">
            <span v-if="values.length && !isOpen" class="multiselect__single">
              เลือกแล้ว {{ values.length }} ประเภท
            </span>
          </template>
          <template slot="tag" slot-scope=""></template>
          <template slot="noResult">
            <span>ไม่มีข้อมูล</span>
          </template>
        </multiselect>
      </b-col>
      <b-col class="mb-2 text-right text-md-left align-self-end" cols="12" lg="3" md="6">
        <b-button class="mr-2" type="submit" variant="primary">
          ค้นหา
        </b-button>
        <b-button type="button" variant="light" @click="onReset">
          ล้างค่า
        </b-button>
      </b-col>
    </b-row>
  </form>
</template>

<script>
import dayjs from 'dayjs';
import {
  PAYMENT_GATEWAY,
  PAYMENT_GATEWAY_BALANCE_LOG_MAX_MONTHS,
  PAYMENT_GATEWAY_BALANCE_LOG_TYPE,
  PAYMENT_GATEWAY_BALANCE_LOG_TYPES
} from '@src/constants/payment-gateway';
import {mapGetters} from 'vuex';

// ค่าคงที่ของ API ใช้กรองเฉพาะแถวที่ระบบทำเอง ไม่ได้อยู่ในรายชื่อจาก /balance-log/actors
const SYSTEM_ACTOR_ID = 'system'

const DEFAULT_DAY_RANGE = 7

export default {
  name: 'PaymentGatewayBalanceLogFilter',
  props: {
    // รายการ payment gateway ที่มองเห็น (มาจาก summary)
    gateways: {
      type: Array,
      default: () => []
    },
    // คนที่เคยขยับยอด มาจาก /payment-gateway/balance-log/actors
    actors: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      SYSTEM_ACTOR_ID,
      maxMonths: PAYMENT_GATEWAY_BALANCE_LOG_MAX_MONTHS,
      typeOptions: PAYMENT_GATEWAY_BALANCE_LOG_TYPES,
      form: this.createDefaultForm(),
    }
  },
  computed: {
    ...mapGetters(['isMaster']),
    minDate() {
      return dayjs().subtract(PAYMENT_GATEWAY_BALANCE_LOG_MAX_MONTHS, 'month').format('YYYY-MM-DD')
    },
    maxDate() {
      return dayjs().format('YYYY-MM-DD')
    },
    gatewayOptions() {
      return this.gateways.map((gateway) => ({
        id: gateway.id,
        label: this.isMaster
          ? `${this.gatewayName(gateway.name)} (${gateway.agentName})`
          : this.gatewayName(gateway.name),
      }))
    }
  },
  created() {
    this.onSubmit()
  },
  methods: {
    createDefaultForm() {
      return {
        from: dayjs().subtract(DEFAULT_DAY_RANGE, 'day').format('YYYY-MM-DD'),
        to: dayjs().format('YYYY-MM-DD'),
        paymentGatewayId: null,
        actorId: null,
        type: [],
      }
    },
    gatewayName(name) {
      return PAYMENT_GATEWAY[name]?.name || name
    },
    typeLabel(type) {
      return PAYMENT_GATEWAY_BALANCE_LOG_TYPE[type]?.label || type
    },
    onReset() {
      this.form = this.createDefaultForm()
      this.onSubmit()
    },
    onSubmit() {
      this.$emit('submit', {
        paymentGatewayId: this.form.paymentGatewayId,
        actorId: this.form.actorId,
        type: this.form.type,
        // datepicker ให้ค่าเป็น YYYY-MM-DD ตีความเป็นเวลาท้องถิ่น แล้วส่งเป็น UTC instant ให้ backend
        from: dayjs(this.form.from).startOf('day').toISOString(),
        // API รวมปลายทาง ต้องส่งท้ายวันเพื่อให้ได้ทั้งวันของ to ตามเวลาท้องถิ่น
        to: dayjs(this.form.to).endOf('day').toISOString(),
      })
    }
  }
}
</script>
